import { isPast, parseISO } from "date-fns";
import { departmentMatchesFilter } from "../constants/departments";

export function normalizeOpenCallId(id) {
  return id != null ? String(id) : "";
}

export function getOpenCallEventId(opencall) {
  const eventRef = opencall?.event;
  if (!eventRef) return null;
  if (typeof eventRef === "object" && eventRef._id != null) {
    return String(eventRef._id);
  }
  return String(eventRef);
}

export function getOpenCallVoteCount(opencall) {
  if (typeof opencall?.voterCount === "number") {
    return opencall.voterCount;
  }
  return Array.isArray(opencall?.voters) ? opencall.voters.length : 0;
}

export function openCallUserHasVoted(opencall, userId) {
  if (!userId) return false;
  if (typeof opencall?.currentUserHasVoted === "boolean") {
    return opencall.currentUserHasVoted;
  }
  const voterIds = opencall?.voters || [];
  return voterIds.some((id) => String(id) === String(userId));
}

/** User ids needed to render names/avatars for the given open calls. */
export function collectOpenCallUserIds(openCalls, { includeVoters = false } = {}) {
  const ids = new Set();

  (openCalls || []).forEach((opencall) => {
    (opencall.shots || []).forEach((shot) => {
      if (shot?.user) ids.add(String(shot.user));
    });
    (opencall.winners || []).forEach((winnerId) => {
      if (winnerId) ids.add(String(winnerId));
    });
    if (includeVoters) {
      (opencall.voters || []).forEach((voterId) => {
        if (voterId) ids.add(String(voterId));
      });
    }
  });

  return [...ids];
}

export function getShotCountClass(shotsCount, totalSpots) {
  const totalSpotsNum = parseInt(totalSpots, 10) || 0;
  if (totalSpotsNum === 0) return "shots-medium";

  if (shotsCount >= totalSpotsNum) {
    return "shots-high";
  }
  if (shotsCount >= totalSpotsNum / 2) {
    return "shots-medium";
  }
  return "shots-low";
}

/**
 * Categorize open calls the same way as OpenCallList.jsx.
 * @param {Array} openCalls
 * @param {Record<string, object>} eventsByOpenCallId - keyed by open call _id
 */
export function categorizeOpenCalls(openCalls, eventsByOpenCallId) {
  const ongoing = [];
  const voting = [];
  const past = [];
  const canceled = [];

  for (const opencall of openCalls) {
    const openCallId = normalizeOpenCallId(opencall._id);
    if (!eventsByOpenCallId[openCallId]) continue;

    if (opencall.status === "Closed") {
      past.push(opencall);
      continue;
    }

    if (opencall.status === "Cancelled") {
      canceled.push(opencall);
      continue;
    }

    if (!opencall.openCallDeadline) continue;

    const deadline = parseISO(opencall.openCallDeadline);
    if (isPast(deadline) && !opencall.openCallSpread) {
      voting.push(opencall);
    } else {
      ongoing.push(opencall);
    }
  }

  const getEventDate = (opencall) => {
    const event = eventsByOpenCallId[normalizeOpenCallId(opencall._id)];
    return event?.date ? new Date(event.date) : new Date(0);
  };

  const byDateDesc = (a, b) => getEventDate(b) - getEventDate(a);
  ongoing.sort(byDateDesc);
  voting.sort(byDateDesc);
  past.sort(byDateDesc);
  canceled.sort(byDateDesc);

  return { ongoing, voting, past, canceled };
}

/**
 * Ongoing + voting buckets for non-closed open calls (active list API).
 * Closed calls belong on the paginated closed endpoint only.
 */
export function categorizeActiveOpenCalls(openCalls, eventsByOpenCallId) {
  const activeOnly = (openCalls || []).filter(
    (opencall) => opencall.status !== "Closed" && opencall.status !== "Cancelled",
  );
  const { ongoing, voting, canceled } = categorizeOpenCalls(
    activeOnly,
    eventsByOpenCallId,
  );
  return { ongoing, voting, canceled };
}

export function buildEventsByOpenCallId(openCalls, events) {
  const eventById = new Map(
    (events || []).map((event) => [String(event._id), event]),
  );
  const map = {};

  (openCalls || []).forEach((opencall) => {
    const eventId = getOpenCallEventId(opencall);
    if (eventId && eventById.has(eventId)) {
      map[normalizeOpenCallId(opencall._id)] = eventById.get(eventId);
    }
  });

  return map;
}

/**
 * Build event map from /opencall/list responses that include eventData.
 */
export function buildEventsByOpenCallIdFromList(openCalls) {
  const map = {};

  (openCalls || []).forEach((opencall) => {
    if (!opencall?.eventData) return;
    map[normalizeOpenCallId(opencall._id)] = opencall.eventData;
  });

  return map;
}

export function filterOpenCallsForDepartment(
  openCalls,
  events,
  departmentRef,
) {
  if (!departmentRef) return [];

  return (openCalls || []).filter((opencall) => {
    const eventId = getOpenCallEventId(opencall);
    if (!eventId) return false;

    const event = (events || []).find(
      (entry) => String(entry._id) === eventId,
    );
    if (!event) return false;

    return event.departments?.some((dep) =>
      departmentMatchesFilter(dep, departmentRef),
    );
  });
}

export function countRecruitingOpenCalls(ongoing, voting) {
  return (ongoing?.length || 0) + (voting?.length || 0);
}

/**
 * Normalize votes from API (plain object) or Mongoose Map-like values.
 */
export function normalizeOpenCallVotes(votes) {
  if (!votes) return {};

  if (votes instanceof Map) {
    return Object.fromEntries(votes.entries());
  }

  if (typeof votes === "object") {
    return Object.fromEntries(
      Object.entries(votes).map(([id, count]) => [String(id), Number(count) || 0]),
    );
  }

  return {};
}

/**
 * Split vote results into automatic winners and tie groups that need EM input.
 * @returns {{ automaticWinners: string[], tieGroups: Array<{ voteCount: number, candidates: string[], spotsToFill: number }> }}
 */
export function getOpenCallTieGroups(votes, spots) {
  const votesMap = normalizeOpenCallVotes(votes);
  const spotsNum = parseInt(spots, 10) || 0;

  if (spotsNum <= 0 || Object.keys(votesMap).length === 0) {
    return { automaticWinners: [], tieGroups: [] };
  }

  const sorted = Object.entries(votesMap).sort(
    (a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])),
  );

  const automaticWinners = [];
  const tieGroups = [];
  let spotsRemaining = spotsNum;
  let index = 0;

  while (index < sorted.length && spotsRemaining > 0) {
    const voteCount = sorted[index][1];
    const tier = [];

    while (index < sorted.length && sorted[index][1] === voteCount) {
      tier.push(String(sorted[index][0]));
      index += 1;
    }

    if (tier.length <= spotsRemaining) {
      automaticWinners.push(...tier);
      spotsRemaining -= tier.length;
    } else {
      tieGroups.push({
        voteCount,
        candidates: tier,
        spotsToFill: spotsRemaining,
      });
      spotsRemaining = 0;
    }
  }

  return { automaticWinners, tieGroups };
}

export function hasOpenCallTies(votes, spots) {
  return getOpenCallTieGroups(votes, spots).tieGroups.length > 0;
}

/**
 * Resolve winners from votes, optional tie-breaker selections keyed by group index.
 * @param {Record<number, string[]>} tieSelections
 */
export function resolveOpenCallWinners(votes, spots, tieSelections = {}) {
  const { automaticWinners, tieGroups } = getOpenCallTieGroups(votes, spots);

  const tieWinners = tieGroups.flatMap((_, groupIndex) =>
    (tieSelections[groupIndex] || []).map(String),
  );

  return [...automaticWinners, ...tieWinners];
}

/**
 * Legacy helper: sort by votes and take top N (used when there are no ties).
 */
export function sortOpenCallWinners(votes, spots) {
  return resolveOpenCallWinners(votes, spots);
}
